import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Button,
    Card,
    Input,
    Checkbox,
    Modal,
    Form,
    Tag,
    Space,
    message,
    Popconfirm,
    Empty,
} from 'antd'
import {
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    SyncOutlined,
    ArrowLeftOutlined,
} from '@ant-design/icons'
import deviceApi from '../services/api'
import TimeUtils from '../utils'
import './goals.css'

const { TextArea } = Input

function Goals() {
    const navigate = useNavigate()
    const [goals, setGoals] = useState([])
    const [loading, setLoading] = useState(true)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingGoal, setEditingGoal] = useState(null)
    const [form] = Form.useForm()
    const [newTaskTexts, setNewTaskTexts] = useState({})

    const loadGoals = useCallback(async () => {
        try {
            const data = await deviceApi.getGoals()
            setGoals(data || [])
        } catch (error) {
            message.error(`加载失败: ${error.message}`)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        loadGoals()
    }, [loadGoals])

    const handleCreate = () => {
        setEditingGoal(null)
        form.resetFields()
        setModalOpen(true)
    }

    const handleEdit = (goal) => {
        setEditingGoal(goal)
        form.setFieldsValue({
            title: goal.title,
            description: goal.description,
        })
        setModalOpen(true)
    }

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields()
            if (editingGoal) {
                await deviceApi.updateGoal(editingGoal.id, {
                    title: values.title,
                    description: values.description || '',
                })
            } else {
                await deviceApi.createGoal({
                    title: values.title,
                    description: values.description || '',
                    tasks: [],
                })
            }
            setModalOpen(false)
            loadGoals()
        } catch (error) {
            if (error.errorFields) return
            message.error(`保存失败: ${error.message}`)
        }
    }

    const handleDelete = async (goalId) => {
        try {
            await deviceApi.deleteGoal(goalId)
            message.success('已删除')
            loadGoals()
        } catch (error) {
            message.error(`删除失败: ${error.message}`)
        }
    }

    const updateTasks = async (goal, newTasks) => {
        try {
            await deviceApi.updateGoal(goal.id, { tasks: newTasks })
            setGoals(prev => prev.map(g =>
                g.id === goal.id ? { ...g, tasks: newTasks } : g
            ))
        } catch (error) {
            message.error(`更新失败: ${error.message}`)
        }
    }

    const handleTaskToggle = (goal, taskId) => {
        const newTasks = goal.tasks.map(t =>
            t.id === taskId ? { ...t, done: !t.done } : t
        )
        updateTasks(goal, newTasks)
    }

    const handleTaskDelete = (goal, taskId) => {
        const newTasks = goal.tasks.filter(t => t.id !== taskId)
        updateTasks(goal, newTasks)
    }

    const handleTaskEdit = (goal, taskId, newContent) => {
        const newTasks = goal.tasks.map(t =>
            t.id === taskId ? { ...t, content: newContent } : t
        )
        updateTasks(goal, newTasks)
    }

    const handleAddTask = (goal) => {
        const text = (newTaskTexts[goal.id] || '').trim()
        if (!text) return
        const maxId = goal.tasks.length > 0
            ? Math.max(...goal.tasks.map(t => t.id))
            : 0
        const newTasks = [...goal.tasks, { id: maxId + 1, content: text, done: false }]
        updateTasks(goal, newTasks)
        setNewTaskTexts(prev => ({ ...prev, [goal.id]: '' }))
    }

    const handleSync = async (goal, task) => {
        try {
            const today = TimeUtils.today()
            await deviceApi.syncGoalTask({
                date: today,
                project: goal.title,
                plan: task.content,
            })
            message.success(`已同步到 ${today}`)
        } catch (error) {
            message.error(`同步失败: ${error.message}`)
        }
    }

    const handleStatusChange = async (goal, newStatus) => {
        try {
            await deviceApi.updateGoal(goal.id, { status: newStatus })
            setGoals(prev => prev.map(g =>
                g.id === goal.id ? { ...g, status: newStatus } : g
            ))
        } catch (error) {
            message.error(`更新状态失败: ${error.message}`)
        }
    }

    const statusColor = { active: 'blue', completed: 'green', archived: 'default' }
    const statusLabel = { active: '进行中', completed: '已完成', archived: '已归档' }

    if (loading) {
        return <div className="goals-container">加载中...</div>
    }

    return (
        <div className="goals-container">
            <div className="goals-header">
                <h1>长期目标</h1>
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/edit')}>
                        返回编辑
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                        新建目标
                    </Button>
                </Space>
            </div>

            {goals.length === 0 ? (
                <Empty description="还没有长期目标，点击上方按钮创建" />
            ) : (
                goals.map(goal => (
                    <Card
                        key={goal.id}
                        className="goal-card"
                        title={
                            <Space>
                                <span>{goal.title}</span>
                                <Tag color={statusColor[goal.status] || 'blue'}>
                                    {statusLabel[goal.status] || goal.status}
                                </Tag>
                            </Space>
                        }
                        extra={
                            <Space>
                                {goal.status === 'active' && (
                                    <Button size="small" onClick={() => handleStatusChange(goal, 'completed')}>
                                        标记完成
                                    </Button>
                                )}
                                {goal.status === 'completed' && (
                                    <Button size="small" onClick={() => handleStatusChange(goal, 'active')}>
                                        重新激活
                                    </Button>
                                )}
                                <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(goal)} />
                                <Popconfirm title="确定删除？" onConfirm={() => handleDelete(goal.id)}>
                                    <Button size="small" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                            </Space>
                        }
                    >
                        {goal.description && (
                            <div className="goal-description">{goal.description}</div>
                        )}

                        {goal.tasks.map(task => (
                            <div key={task.id} className="goal-task-item">
                                <Checkbox
                                    checked={task.done}
                                    onChange={() => handleTaskToggle(goal, task.id)}
                                />
                                <Input
                                    className={`task-content${task.done ? ' done' : ''}`}
                                    variant="borderless"
                                    value={task.content}
                                    onChange={e => handleTaskEdit(goal, task.id, e.target.value)}
                                />
                                <div className="goal-task-actions">
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<SyncOutlined />}
                                        title="同步到今天"
                                        onClick={() => handleSync(goal, task)}
                                    />
                                    <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleTaskDelete(goal, task.id)}
                                    />
                                </div>
                            </div>
                        ))}

                        <div className="add-task-row">
                            <Input
                                placeholder="添加新任务..."
                                value={newTaskTexts[goal.id] || ''}
                                onChange={e => setNewTaskTexts(prev => ({
                                    ...prev,
                                    [goal.id]: e.target.value,
                                }))}
                                onPressEnter={() => handleAddTask(goal)}
                            />
                            <Button
                                icon={<PlusOutlined />}
                                onClick={() => handleAddTask(goal)}
                            >
                                添加
                            </Button>
                        </div>
                    </Card>
                ))
            )}

            <Modal
                title={editingGoal ? '编辑目标' : '新建目标'}
                open={modalOpen}
                onOk={handleModalOk}
                onCancel={() => setModalOpen(false)}
                okText="保存"
                cancelText="取消"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="标题"
                        name="title"
                        rules={[{ required: true, message: '请输入标题' }]}
                    >
                        <Input placeholder="目标名称" />
                    </Form.Item>
                    <Form.Item label="描述" name="description">
                        <TextArea
                            autoSize={{ minRows: 3, maxRows: 6 }}
                            placeholder="目标详情描述（可选）"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}

export default Goals
